import { deferredCallback } from './helpers/deferred-callback';
import { focusScreenreaderOnElement } from './helpers/focus-on-element';
import { scrollToElement } from './helpers/scroll-to-element';

const defaultRevealErrorMessageAttr = 'data-reveal-error-message';
const defaultRevealErrorTriggerAttr = 'data-reveal-error-trigger';

interface RevealErrorOptions {
    defer?: boolean;
    message?: string;
    dataId?: string;
    focus?: boolean;
}

// hook to reveal an error message in the DOM
// based on a data attribute, e.g. data-reveal-error-message
export const useRevealErrorMessage = (
    dataAttr: string = defaultRevealErrorMessageAttr,
    triggerDataAttr: string = defaultRevealErrorTriggerAttr,
) => {
    const scrollToError = (
        selector: string,
        message?: string,
    ): HTMLElement | undefined => {
        // find the error messages in the DOM
        const errorMessages = document.querySelectorAll<HTMLElement>(selector);

        // if no error messages are found, do nothing
        if (errorMessages.length === 0) {
            return undefined;
        }

        if (!message) {
            // if no message is provided, scroll to the first error message
            scrollToElement(errorMessages[0]);

            return errorMessages[0];
        }

        let element;

        // otherwise, find the error message that contains the provided message
        errorMessages.forEach(errorMessage => {
            if (errorMessage.textContent?.includes(message)) {
                scrollToElement(errorMessage);
                element = errorMessage;
            }
        });

        return element;
    };

    const revealError = (options?: RevealErrorOptions) => {
        const { defer = false, message, dataId, focus } = options || {};

        // wrapin a deferred callback to allow for the error message to be
        // rendered before scrolling to it
        deferredCallback(defer, () => {
            // construct the selector based on the dataId
            const selector = dataId
                ? `[${dataAttr}="${dataId}"]`
                : `[${dataAttr}]`;

            // scroll to the error message
            const element = scrollToError(selector, message);

            // if the focus option is set, focus on the error message
            if (element && focus) {
                const trigger = document.querySelector(
                    `[${triggerDataAttr}]`,
                ) as HTMLElement | null;
                focusScreenreaderOnElement(element, trigger);
            }
        });
    };

    return {
        revealError,
    };
};
